/**
 * ScrollClass
 * Add/Remove JavaScript classes based on scroll position
 *
 * @class ScrollClass
 * @author EN GARDE
 * @version 1.2
 *
 * Change Log:
 * 2.0  refactored to use native js instead of jquery.
 * 1.2	fixed "force" param in calc()
 * 1.1	added "force" param to calc()
 * 		removed unused this.pageInfo property
 * 1.0	initial version
 */

import PageInfo from './PageInfo.js';

import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';

class ScrollClass {

	options = {
		/**
     * Class to apply/remove
     * @type {string}
     */
		class: 'en_is_active',

		/**
     * function will run if viewport or document size changes
     * do the _heavy_ calculation and save it into the data object
     * @type {function}
     * @param {object} data – the data stored by calc()
     * @param {object} pageInfo – infos on the viewport/document size
     * @param {ScrollClass} that – instance
     * @param {boolean} force – force recalc
     */
		cbCalc: ( data, pageInfo, that, force ) => { // eslint-disable-line no-unused-vars

		},

		/**
     * Should the element be active based on the given data?
     * @type {function}
     * @param {object} data – the data stored by calc()
     * @param {object} pageInfo – infos on the viewport/document size
     * @param {ScrollClass} that – instance
     * @return {boolean}
     */
		cbIsActive: ( data, pageInfo, that ) => { // eslint-disable-line no-unused-vars
			return ( typeof data.start === 'undefined' || data.start < pageInfo.vpTop ) &&
          ( typeof data.end === 'undefined' || pageInfo.vpTop <  data.end );
		}
	};


	data = {
		start: 0,
		end: 0,
		active: null
	};

	animationFrameId = null;
	appliedPageInfo = {};

	constructor( element, {data = {}, ...options} ) {

		this.element = element;
		this.options = Object.assign( {}, this.options, options );

		// Set preliminary data.
		this.data = Object.assign( {}, this.data, data );

		// ready if element exists.
		if ( this.element ) {
			this.init();
		}
	}

	/**
   * Init component
   * @memberof ScrollClass
   */
	init() {

		// initial call
		this.calc();

		// update on scroll
		window.addEventListener( 'scroll', throttle( () => this.calc(), 100 ) );

		// in rare cases the element is not recalculated after scrolling rapidly
		window.addEventListener( 'scroll', debounce( () => this.calc(), 250 ) );

		// use requestAnimationFrame to paint changes to DOM
		// if there is already a loop running, reset it
		if ( window.requestAnimationFrame ) {
			if ( this.animationFrameId ) {
				window.cancelAnimationFrame( this.animationFrameId );
			}

			const loop = () => {
				this.animationFrameId = window.requestAnimationFrame( () => loop() );
				this.apply();
			};

			// Start the Loop.
			loop();
		} else {
			// on older browsers: use interval instead
			window.addEventListener( 'scroll', throttle( () => this.apply(), 600 ) );
		}
	}

	/**
   * Apply changes to DOM
   * this function will run permanently on every frame, so no heavy stuff here
   * @memberof ScrollClass
   */
	apply() {
		const className = this.options.class;
		const isActive  = this.options.cbIsActive( this.data, this.appliedPageInfo, this );

		// apply class to each element.
		if ( this.data.active !== isActive ) {
			this.element.classList.toggle( className, isActive );
		}

		// save
		this.data.active = isActive;

		// successfully applied changes
		return true;
	}

	/**
   * Detect changes, update values
   * This is where the heavy lifting should be done, but no changes to the DOM
   * @memberof ScrollClass
   */
	calc( force ) {
		const pageInfo = PageInfo.get();

		if ( !! force || ! PageInfo.match( pageInfo, this.appliedPageInfo ) ) {
			this.options.cbCalc( this.data, pageInfo, this, !! force );
		}

		this.appliedPageInfo = pageInfo;
	}
}

export default ScrollClass;
